import { isValiError } from 'valibot';

import {
  playerReady,
  registerPlayerEvents,
  unregisterPlayerEvents,
} from './playerEvents.ts';
import {
  ChangeRepeatModeRequestArgs,
  ChangeVolumeRequestArgs,
  CurrentPlayerStatus,
  LibraryTopResponse,
  NotificationPayloadType,
  NotificationValueMap,
  PlaylistList,
  PlayMediaItemRequestArgs,
  PlayToRequestArgs,
  PlayTrackAtRequestArgs,
  Queue,
  Result,
  SeekToTimeRequestArgs,
} from './types.ts';
import {
  isMKError,
  isQueue,
  isQueueIndex,
  isValidQueue,
  L,
  PLAYER_ASSERT,
  toErr,
  toMediaType,
  toOk,
} from './utils.ts';
import {
  AlbumItem,
  ArtistItem,
  ArtistList,
  RecommendList,
  TrackItem,
} from './apiSchema.ts';
import {
  $requestAuthToken,
  AuthTokenRequestArgs,
  AuthTokenResponse,
} from './api/authToken.ts';
import {
  $requestAuthSigning,
  AuthSigningRequestArgs,
  AuthSigningResponse,
} from './api/authSigning.ts';
import {
  $getMeLibraryPlaylists,
  MeLibraryPlaylistsRequestArgs,
} from './api/meLibraryPlaylists.ts';
import {
  $getMeLibraryRecentlyAdded,
  MeLibraryRecentlyAddedRequestArgs,
} from './api/meLibraryRecentlyAdded.ts';
import {
  $getMeRecommendationsId,
  RecommendationRequestArgs,
} from './api/meRecommendationsId.ts';
import {
  $getPlaylistMedia,
  PlaylistMediaPageRequestArgs,
  PlaylistMediaPageResponse,
} from './api/playlistMedia.ts';
import {
  $getMeLibraryArtists,
  MeLibraryArtistsRequestArgsInput,
} from './api/meLibraryArtists.ts';
import {
  $getMeLibraryArtistWithAlbums,
  LibraryArtistRequestArgsInput,
  LibraryArtistResponse,
} from './api/meLibraryArtistWithAlbums.ts';
import {
  $getMeLibraryAlbums,
  MeLibraryAlbumsRequestArgs,
  MeLibraryAlbumsResponse,
} from './api/meLibraryAlbums.ts';
import {
  $getAlbumMedia,
  AlbumMediaPageRequestArgs,
  AlbumMediaPageResponse,
} from './api/albumMedia.ts';
import {
  $getMeRecommendations,
  $getMeRecommendGroups,
  MeRecommendationsRequestArgs,
  RecommendGroupsResponse,
} from './api/meRecommendations.ts';
import {
  $getMeRecentlyPlayed,
  MeRecentlyPlayedRequestArgsInput,
  RecentlyPlayedResponse,
} from './api/meRecentlyPlayed.ts';
import {
  $getMeLibrarySongs,
  LibrarySongsResponse,
  MeLibrarySongsRequestArgs,
} from './api/meLibrarySongs.ts';
import {
  $getCatalogSearch,
  SearchRequestArgs,
  SearchResponse,
} from './api/catalogSearch.ts';
import {
  $getArtistMediaPageData,
  ArtistMediaPageRequestArgs,
  ArtistMediaPageResponse,
} from './api/catalogArtistsId.ts';
import {
  $getCatalogArtistsIdView,
  CatalogArtistsIdViewRequestArgs,
  CatalogArtistsIdViewResponse,
} from './api/catalogArtistsIdView.ts';
import {
  filterUnsupportedItems,
  isPaused,
  queueToTrackItem,
  toContainerMediaItem,
} from './playerUtils.ts';
import {
  $getResourceRelationship,
  ResourceRelationshipRequestArgs,
} from './api/resourceRelationship.ts';
import { $restore, RestoreRequestArgs } from './api/restore.ts';

/**
 * Utility function to execute a function with the MusicKit player instance.
 * @param {(MusicKit.MusicKitInstance, Args) => Promise<T>} fn Function to execute with the player instance
 * @param {unknown[]} args Arguments to pass to the function
 * @param {(res: T) => V} normalizeCallback Callback to normalize the result
 * @returns {Promise<Result<V>>} A promise that resolves with the result of the function or an error
 */
export const withPlayer = async <
  Args extends unknown[],
  T,
  V extends object = never,
>(
  fn: (player: MusicKit.MusicKitInstance, ...args: Args) => Promise<T>,
  args: Args,
  normalizeCallback?: (res: T) => V,
): Promise<Result<V>> => {
  try {
    const player = MusicKit.getInstance();
    PLAYER_ASSERT(player);
    const result = await fn(player, ...args);

    if (typeof normalizeCallback === 'function') {
      const normalized = normalizeCallback(result);
      return toOk(normalized);
    } else if (result) {
      return toOk(result as V);
    }
    return toOk();
  } catch (e) {
    if (isMKError(e)) {
      L.error(`[MKError] ${(e as MusicKit.MKError).message}`);
    } else if (isValiError(e)) {
      // Validation error
    } else {
      L.error('[UnknownError]', (e as Error).message);
    }
    return toErr(e);
  }
};

/**
 * Request the signing key from the license server.
 * @param {AuthSigningParam} value The parameters for the request.
 * @returns {Promise<Result<AuthSigningResponse>>} A promise that resolves with the signing key response.
 */
export const requestAuthSigning = async (
  value: AuthSigningRequestArgs,
): Promise<Result<AuthSigningResponse>> => {
  try {
    const res = await $requestAuthSigning(value);
    return toOk(res);
  } catch (e) {
    if (isValiError(e)) {
      L.error(
        '[ValiError]',
        JSON.stringify(e.issues, null, 2),
      );
    } else {
      L.error('[UnknownError]', (e as Error).message);
    }
    return toErr(e);
  }
};

/**
 * Request an authentication token from the license server.
 * @param {AuthTokenParam} value The parameters for the request
 * @returns
 */
export const requestAuthToken = async (
  value: AuthTokenRequestArgs,
): Promise<Result<AuthTokenResponse>> => {
  try {
    const res = await $requestAuthToken(value);
    return toOk(res);
  } catch (e) {
    if (isValiError(e)) {
      L.error(
        '[ValiError]',
        JSON.stringify(e.issues, null, 2),
      );
    } else {
      L.error('[UnknownError]', (e as Error).message);
    }
    return toErr(e);
  }
};

export const configureMusicKit = async (
  token: string,
) => {
  if (MusicKit.getInstance() && amusic.revision > 1) {
    unregisterPlayerEvents(MusicKit.getInstance());
  }
  await MusicKit.configure({
    developerToken: token,
    app: {
      name: 'A Music',
      build: '1',
    },
    bitrate: MusicKit.PlaybackBitrate.STANDARD,
  });

  const newPlayer = MusicKit.getInstance();
  if (newPlayer) {
    registerPlayerEvents(newPlayer);
    playerReady(newPlayer.isAuthorized);
  } else {
    L.error('Failed to get MusicKit instance');
  }
};

/**
 * Initialize MusicKit with the provided developer token.
 * @param token Developer token for MusicKit
 * @returns {Promise<void>} A promise that resolves when MusicKit is configured
 */
export const initializeMusicKit = async (
  payload:
    NotificationValueMap[typeof NotificationPayloadType.NotifyDeveloperToken],
) => {
  const { token } = payload;
  if (!token) {
    L.error('Received empty developer token');
    return;
  }

  try {
    amusic.revision = amusic.revision + 1;

    if (
      amusic.isMusicKitLoaded && amusic.revision > 1 && MusicKit.getInstance()
    ) {
      L.info('MusicKit instance is already loaded. ');

      if (MusicKit.getInstance().isPlaying) {
        L.warn(
          'MusicKit is currently in a playing state. Reconfiguration will be deferred until the current track ends.',
        );
        amusic.tokenShouldBeRefreshed = true;
        amusic.nextToken = token;
        return;
      } else if (isPaused(MusicKit.getInstance())) {
        L.warn(
          'MusicKit is currently paused with a non-empty queue. Reconfiguration will occur soon.',
        );
        const previousPlayer = MusicKit.getInstance();
        const nowPlayingItemIndex = previousPlayer.nowPlayingItemIndex;
        const currentItem = previousPlayer.queue?.items?.[nowPlayingItemIndex];
        if (!currentItem) {
          await configureMusicKit(token);
          return;
        }

        amusic.backupQueue =
          previousPlayer.queue?.items?.filter(filterUnsupportedItems)
            .map((item) => ({
              type: item.type,
              id: item.id,
            })) ?? [];
        const currentItemIndex = amusic.backupQueue.findIndex((item) =>
          item.id === currentItem.id
        );
        const currentPlaybackTime = previousPlayer.currentPlaybackTime;

        await configureMusicKit(token);

        if (amusic.backupQueue.length === 0) return;

        const newPlayer = MusicKit.getInstance();
        const queue = amusic.backupQueue.map((item) => item.id);
        await newPlayer.setQueue({ songs: queue }); // Support only songs queue

        if (currentItemIndex >= 0 && currentItemIndex <= queue.length - 1) {
          await newPlayer.changeToMediaAtIndex(currentItemIndex);
          await newPlayer.seekToTime(currentPlaybackTime);
          await newPlayer.pause();
        } else {
          L.warn(
            'Current item index is out of range. The queue will be reset.',
          );
          await newPlayer.clearQueue();
        }

        amusic.backupQueue = null;
        amusic.tokenShouldBeRefreshed = false;
        amusic.nextToken = null;
        return;
      }

      L.info('Reconfiguring MusicKit with new developer token.');
    }

    await configureMusicKit(token);
  } catch (e) {
    L.error('Failed to configure MusicKit:', (e as MusicKit.MKError).message);
    location.reload();
  }
};

/**
 * Authorize the user with MusicKit.
 * @returns {Promise<Result<never>>} A promise that resolves when the user is authorized
 */
export const authorize = (): Promise<Result<never>> =>
  withPlayer(
    async (player) => {
      await player.authorize();
    },
    [],
  );

/**
 * Unauthorize the user from MusicKit.
 * @returns {Promise<Result<never>>} A promise that resolves when the user is unauthorized
 */
export const unauthorize = (): Promise<Result<never>> =>
  withPlayer(
    async (player) => {
      await player.unauthorize();
    },
    [],
  );

/**
 * Play the current media item.
 * @returns {Promise<Result<never>>} A promise that resolves when playback starts
 */
export const play = (): Promise<Result<never>> =>
  withPlayer(
    async (player) => {
      await player.play();
    },
    [],
  );

/**
 * Pause the current media item.
 * @returns {Promise<Result<never>>} A promise that resolves when playback is paused
 */
export const pause = (): Promise<Result<never>> =>
  withPlayer(
    async (player) => {
      await player.pause();
    },
    [],
  );

/**
 * Stop the current media item.
 * @returns {Promise<Result<never>>} A promise that resolves when playback is stopped
 */
export const stop = (): Promise<Result<never>> =>
  withPlayer(
    async (player) => {
      await player.stop();
    },
    [],
  );

/**
 * Skip to the next item in the playback queue.
 * @returns {Promise<Result<never>>} A promise that resolves when playback is skipped to the next item
 */
export const skipToNextItem = (): Promise<Result<never>> =>
  withPlayer(
    async (player) => {
      await player.skipToNextItem();
    },
    [],
  );

/**
 * Skip to the previous item in the playback queue.
 * @returns {Promise<Result<never>>} A promise that resolves when playback is skipped to the previous item
 */
export const skipToPreviousItem = (): Promise<Result<never>> =>
  withPlayer(
    async (player) => {
      await player.skipToPreviousItem();
    },
    [],
  );

/**
 * Seek to a specific time in the current media item.
 * @param value Request arguments for seeking to a specific time
 * @returns A promise that resolves when seeking is complete
 */
export const seekToTime = (
  value: SeekToTimeRequestArgs,
): Promise<Result<never>> =>
  withPlayer(
    async (player, value: SeekToTimeRequestArgs) => {
      const { time } = value;
      if (typeof time !== 'number' || time < 0) {
        throw new TypeError('[seekToTime] Invalid time value.');
      }
      if (player.queueIsEmpty) {
        throw new Error('[seekToTime] Queue is empty.');
      }

      await player.seekToTime(time);
    },
    [value],
  );

/**
 * Play a track at a specific position in the queue.
 * @param {PlayTrackAtRequestArgs} value Request arguments for playing a track at a specific position
 * @returns {Promise<Result<never>>} A promise that resolves when playback starts
 */
export const playTrackAt = (
  value: PlayTrackAtRequestArgs,
): Promise<Result<never>> =>
  withPlayer(
    async (player, value) => {
      const { position } = value;
      if (
        typeof position !== 'number' || position < 0 ||
        player.queue.length - 1 < position
      ) {
        throw new TypeError('[playTrackAt] Invalid position.');
      }
      if (player.queueIsEmpty) {
        throw new Error('[playTrackAt] Queue is empty.');
      }
      await player.changeToMediaAtIndex(position);
    },
    [value],
  );

/**
 * Play a media items(playlist, album, station) or a track from a specific queue.
 * A media items plays a track from the head of the queue.
 * @param {PlayToRequestArgs} value Request arguments for playing a media items or a track.
 * @returns {Promise<Result<never>>} A promise that resolves when playback starts
 */
export const playTo = (
  value: PlayToRequestArgs,
): Promise<Result<never>> =>
  withPlayer(
    async (player, value) => {
      const { queue } = value;
      if (!isQueue(queue)) {
        throw new TypeError('[playTo] Invalid queue.');
      }
      await player.setQueue({
        [toMediaType(queue.type)]: queue.id,
      });
      await player.play();
    },
    [value],
  );

/**
 * Play a media item from a specific queue.
 * @param {PlayMediaItemRequestArgs} value Request arguments for playing a media item
 * @returns {Promise<Result<never>>} A promise that resolves when playback starts
 */
export const playToMediaItem = (
  value: PlayMediaItemRequestArgs,
): Promise<Result<never>> =>
  withPlayer(
    async (player, value: PlayMediaItemRequestArgs) => {
      const { trackListQueue, trackItemQueue } = value;

      if (!isValidQueue(trackListQueue)) {
        throw new TypeError('[playToMediaItem] Invalid track list queue.');
      }
      if (!isValidQueue(trackItemQueue)) {
        throw new TypeError('[playToMediaItem] Invalid track item queue.');
      }

      const mediaItems = await player.setQueue({
        [toMediaType(trackListQueue.type)]: trackListQueue.id,
      });

      if (mediaItems.length === 0) {
        throw new Error('[playToMediaItem] No media items found.');
      }

      if (isQueueIndex(trackItemQueue)) {
        const { index } = trackItemQueue;
        if (index >= mediaItems.length) {
          throw new RangeError(
            `[playToMediaItem] Index out of range: ${index} >= ${mediaItems.length}`,
          );
        }
        await player.changeToMediaAtIndex(index);
      } else if (isQueue(trackItemQueue)) {
        const { id } = trackItemQueue;
        const item = mediaItems.items.find((item: MusicKit.MediaItem) => {
          if (item.id === id) {
            return true;
          }
          return false;
        });
        if (!item) {
          throw new Error(
            `[playToMediaItem] Media item not found: ${
              JSON.stringify(
                trackItemQueue,
              )
            }`,
          );
        }
        await player.changeToMediaItem(item);
      } else {
        throw new TypeError(
          '[playToMediaItem] Invalid track item queue type.',
        );
      }
    },
    [value],
  );

/**
 * Request to play the next item in the queue.
 * @param {Queue} value Request arguments for playing the next item in the queue
 * @returns {Promise<Result<never>>} A promise that resolves when the queue is updated.
 */
export const playNext = (
  value: Queue,
): Promise<Result<never>> =>
  withPlayer(
    async (player, value: Queue) => {
      if (!isQueue(value)) {
        throw new TypeError('[playNext] Invalid queue.');
      }
      await player.playNext({
        [toMediaType(value.type)]: value.id,
      });
    },
    [value],
  );

/**
 * Request to play a media item later in the queue.
 * @param {Queue} value Request arguments for playing a media item later in the queue
 * @returns {Promise<Result<never>>} A promise that resolves when the queue is updated.
 */
export const playLater = (
  value: Queue,
): Promise<Result<never>> =>
  withPlayer(
    async (player, value: Queue) => {
      if (!isQueue(value)) {
        throw new TypeError('[playLater] Invalid queue.');
      }
      await player.playLater({
        [toMediaType(value.type)]: value.id,
      });
    },
    [value],
  );

/**
 * Request to clear the playback queue.
 * @returns {Promise<Result<never>>} A promise that resolves when the queue is cleared.
 */
export const clearQueue = (): Promise<Result<never>> =>
  withPlayer(
    async (player) => {
      await player.clearQueue();
    },
    [],
  );

/**
 * Request to get the current player status.
 * @returns {Promise<Result<CurrentPlayerStatus>>} A promise that resolves with the current player status.
 */
export const getCurrentPlayerStatus = (): Promise<
  Result<CurrentPlayerStatus>
> =>
  withPlayer(
    async (player: MusicKit.MusicKitInstance): Promise<CurrentPlayerStatus> => {
      const {
        currentPlaybackDuration,
        currentPlaybackProgress,
        currentPlaybackTime,
        currentPlaybackTimeRemaining,
        isAuthorized,
        isPlaying,
        nowPlayingItem,
        nowPlayingItemIndex,
        playbackRate,
        queueIsEmpty,
        queue,
        repeatMode,
        shuffleMode,
        storefrontId,
        volume,
      } = player as unknown as (MusicKit.MusicKitInstance & MusicKit.Player);
      await Promise.resolve();
      return {
        currentPlaybackDuration,
        currentPlaybackProgress,
        currentPlaybackTime,
        currentPlaybackTimeRemaining,
        isAuthorized,
        isPlaying,
        nowPlayingItem: queueToTrackItem(nowPlayingItem),
        nowPlayingItemIndex: nowPlayingItemIndex ?? -1,
        container: toContainerMediaItem(nowPlayingItem),
        playbackRate,
        queueIsEmpty,
        queue: ((queue) => {
          if (!queue) return [] as TrackItem[];
          const items = queue.items;
          if (!items || items.length === 0) {
            return [] as TrackItem[];
          }
          return items.map(queueToTrackItem) as TrackItem[];
        })(queue),
        repeatMode,
        // NOTE: To work around the issue where the type definition for MusicKit.PlayerShuffleMode is not recognized,
        //       we assigned the value 1(== MusicKit.PlayerShuffleMode.songs) directly.
        shuffleMode: shuffleMode === 1,
        storefrontId,
        volume,
      };
    },
    [],
  );

/**
 * Change the repeat mode of the player.
 * @param {ChangeRepeatModeRequestArgs} value Request arguments for changing the repeat mode
 * @returns {Promise<Result<never>>} A promise that resolves when the repeat mode is changed
 */
export const changeRepeatMode = (
  value: ChangeRepeatModeRequestArgs,
): Promise<Result<never>> =>
  withPlayer(
    async (player, value: ChangeRepeatModeRequestArgs) => {
      const { repeatMode } = value;
      if (typeof repeatMode !== 'number') {
        throw new TypeError('[changeRepeatMode] Invalid repeat mode.');
      }
      if (repeatMode < 0 || repeatMode > 2) {
        throw new RangeError(
          `[changeRepeatMode] Invalid repeat mode: ${repeatMode}. Expected 0, 1, or 2.`,
        );
      }
      player.repeatMode = repeatMode as MusicKit.PlayerRepeatMode;
      await Promise.resolve();
    },
    [value],
  );

/**
 * Request to set the volume of the player.
 * @param {number} value Volume level between 0 and 1
 * @returns {Promise<Result<never>>} A promise that resolves when the volume is set
 */
export const changeVolume = (
  value: ChangeVolumeRequestArgs,
): Promise<Result<never>> =>
  withPlayer(
    async (player, value: ChangeVolumeRequestArgs) => {
      const { volume } = value;
      if (typeof volume !== 'number' || volume < 0 || volume > 1) {
        throw new TypeError('[changeVolume] Invalid volume level.');
      }
      player.volume = volume;
      await Promise.resolve();
    },
    [value],
  );

/**
 * Request to mute the player.
 * @returns {Promise<Result<never>>} A promise that resolves when the player is muted
 */
export const mute = (): Promise<Result<never>> =>
  withPlayer(
    async (player: MusicKit.MusicKitInstance) => {
      player.mute();
      await Promise.resolve();
    },
    [],
  );

/**
 * Request to unmute the player.
 * @returns {Promise<Result<never>>} A promise that resolves when the player is unmuted
 */
export const unmute = (): Promise<Result<never>> =>
  withPlayer(
    async (player: MusicKit.MusicKitInstance) => {
      player.unmute();
      await Promise.resolve();
    },
    [],
  );

/**
 * Request to toggle the shuffle mode of the player.
 * @returns {Promise<Result<{ shuffling: boolean }>>} A promise that resolves with the current shuffle mode status
 */
export const toggleShuffleMode = (): Promise<Result<{ shuffling: boolean }>> =>
  withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
    ): Promise<{ shuffling: boolean }> => {
      const currentMode = player.shuffleMode;

      // Toggle the shuffle mode
      const shuffling = currentMode === 0 /* MusicKit.PlayerShuffleMode.off */;

      player.shuffleMode = shuffling
        ? 1 /* MusicKit.PlayerShuffleMode.songs */
        : 0 /* MusicKit.PlayerShuffleMode.off */;

      await Promise.resolve();

      return { shuffling };
    },
    [],
  );

/**
 * Request to restore the player state.
 * @returns {Promise<Result<never>>} A promise that resolves when the player state is restored
 */
export const restore = (value: RestoreRequestArgs): Promise<Result<never>> =>
  withPlayer(
    async (player: MusicKit.MusicKitInstance) => {
      await $restore(player, value);
    },
    [],
  );

/**
 * Fetch the user's recommendations(primary/secondary) data.
 * @returns {Promise<Result<RecommendList[]>>} A promise that resolves with the user's home data
 */
export const getRecommendations = (): Promise<Result<RecommendList[]>> => {
  return withPlayer(
    async (player: MusicKit.MusicKitInstance) => {
      const res = await $getMeRecommendations(player);
      return res;
    },
    [],
  );
};

/**
 * Fetch the user's recommendations groups data.
 * @param {MeRecommendationsRequestArgs} value Request arguments for fetching recommendation groups
 * @returns {Promise<Result<RecommendGroupsResponse>>} A promise that resolves with the user's recommendation groups
 */
export const getRecommendGroups = (
  value: MeRecommendationsRequestArgs,
): Promise<Result<RecommendGroupsResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: MeRecommendationsRequestArgs,
    ) => {
      const res = await $getMeRecommendGroups(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the user's recently played items.
 * @param {MeRecentlyPlayedRequestArgsInput} value Request arguments for fetching recently played items
 * @returns {Promise<Result<RecentlyPlayedResponse>>} A promise that resolves with the user's recently played items
 */
export const getRecentlyPlayed = (
  value: MeRecentlyPlayedRequestArgsInput,
): Promise<Result<RecentlyPlayedResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: MeRecentlyPlayedRequestArgsInput,
    ) => {
      const res = await $getMeRecentlyPlayed(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the user's recommendations.
 * @param {RecommendationRequestArgs} value Request arguments for fetching recommendations
 * @returns {Promise<Result<RecommendList>>} A promise that resolves with the user's recommendations
 */
export const getRecommendationsId = (
  value: RecommendationRequestArgs,
): Promise<Result<RecommendList>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: RecommendationRequestArgs,
    ) => {
      const res = await $getMeRecommendationsId(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the user's library playlists.
 * @param {MeLibraryPlaylistsRequestArgs} value Request arguments for fetching library playlists
 * @returns {Promise<Result<PlaylistList>>} A promise that resolves with the user's library playlists
 */
export const getMeLibraryPlaylists = (
  value: MeLibraryPlaylistsRequestArgs,
): Promise<Result<PlaylistList>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: MeLibraryPlaylistsRequestArgs,
    ) => {
      const res = await $getMeLibraryPlaylists(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the specific playlist and its tracks from the user's library.
 * @param {PlaylistMediaPageRequestArgs} value Request arguments for fetching playlist tracks
 * @returns {Promise<Result<PlaylistMediaPageResponse>>} A promise that resolves with the tracks of the playlist
 */
export const getPlaylistMedia = (
  value: PlaylistMediaPageRequestArgs,
): Promise<Result<PlaylistMediaPageResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: PlaylistMediaPageRequestArgs,
    ) => {
      const res = await $getPlaylistMedia(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the user's recently added items from the library.
 * @param {MeLibraryRecentlyAddedRequestArgs} value Request arguments for fetching recently added items.
 * @returns {Promise<Result<LibraryTopResponse>>} A promise that resolves with the user's recently added items.
 */
export const getMeLibraryRecentlyAdded = (
  value: MeLibraryRecentlyAddedRequestArgs,
): Promise<Result<LibraryTopResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: MeLibraryRecentlyAddedRequestArgs,
    ) => {
      const res = await $getMeLibraryRecentlyAdded(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the user's library albums.
 * @param {MeLibraryAlbumsRequestArgs} value Request arguments for fetching library albums
 * @returns {Promise<Result<MeLibraryAlbumsResponse>>} A promise that resolves with the user's library albums
 */
export const getMeLibraryAlbums = (
  value: MeLibraryAlbumsRequestArgs,
): Promise<Result<MeLibraryAlbumsResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: MeLibraryAlbumsRequestArgs,
    ) => {
      const res = await $getMeLibraryAlbums(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch a specific album with its tracks from the user's library.
 * @param {AlbumMediaPageRequestArgs} value Request arguments for fetching the album and its tracks
 * @returns {Promise<Result<AlbumMediaPageResponse>>} A promise that resolves with the album and its tracks
 */
export const getAlbumMedia = (
  value: AlbumMediaPageRequestArgs,
): Promise<Result<AlbumMediaPageResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: AlbumMediaPageRequestArgs,
    ) => {
      const res = await $getAlbumMedia(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the user's library artists.
 * @param {MeLibraryArtistsRequestArgsInput} value Request arguments for fetching library artists
 * @returns {Promise<Result<ArtistList>>} A promise that resolves with the user's library artists
 */
export const getMeLibraryArtists = (
  value: MeLibraryArtistsRequestArgsInput,
): Promise<Result<ArtistList>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: MeLibraryArtistsRequestArgsInput,
    ) => {
      const res = await $getMeLibraryArtists(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the specific artist and their albums from the user's library.
 * @param {LibraryArtistRequestArgsInput} value Request arguments for fetching the artist and their albums
 * @returns {Promise<Result<LibraryArtistResponse>>} A promise that resolves with the artist and their albums
 */
export const getMeLibraryArtist = (
  value: LibraryArtistRequestArgsInput,
): Promise<Result<LibraryArtistResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: LibraryArtistRequestArgsInput,
    ) => {
      const res = await $getMeLibraryArtistWithAlbums(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the user's library songs.
 * @param {MeLibrarySongsRequestArgs} value Request arguments for fetching library songs
 * @returns {Promise<Result<LibrarySongsResponse>>} A promise that resolves with the user's library songs
 */
export const getMeLibrarySongs = (
  value: MeLibrarySongsRequestArgs,
): Promise<Result<LibrarySongsResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: MeLibrarySongsRequestArgs,
    ) => {
      const res = await $getMeLibrarySongs(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the artist media page data.
 * @param {ArtistMediaPageRequestArgs} value Request arguments for fetching artist media page data
 * @returns {Promise<Result<ArtistMediaPageResponse>>} A promise that resolves with the artist media page data
 */
export const getAritstMedia = (
  value: ArtistMediaPageRequestArgs,
): Promise<Result<ArtistMediaPageResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: ArtistMediaPageRequestArgs,
    ) => {
      const res = await $getArtistMediaPageData(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the artist view data by ID.
 * @param {CatalogArtistsIdViewRequestArgs} value Request arguments for fetching artist view data
 * @returns {Promise<Result<CatalogArtistsIdViewResponse>>} A promise that resolves with the artist view data
 */
export const getCatalogArtistsIdView = (
  value: CatalogArtistsIdViewRequestArgs,
): Promise<Result<CatalogArtistsIdViewResponse>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: CatalogArtistsIdViewRequestArgs,
    ) => {
      const res = await $getCatalogArtistsIdView(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the resource relationship data for a specific track.
 * @param {ResourceRelationshipRequestArgs} value Request arguments for fetching resource relationship data
 * @returns {Promise<Result<AlbumItem | ArtistItem>>} A promise that resolves with the resource relationship data
 */
export const getResourceRelationship = (
  value: ResourceRelationshipRequestArgs,
): Promise<Result<AlbumItem | ArtistItem>> => {
  return withPlayer(
    async (
      player: MusicKit.MusicKitInstance,
      value: ResourceRelationshipRequestArgs,
    ) => {
      const res = await $getResourceRelationship(player, value);
      return res;
    },
    [value],
  );
};

/**
 * Fetch the search results from the catalog.
 * @param {SearchRequestArgs} value Request arguments for fetching search results
 * @returns {Promise<Result<SearchResponse>>} A promise that resolves with the search results
 */
export const search = (
  value: SearchRequestArgs,
): Promise<Result<SearchResponse>> => {
  return withPlayer(
    async (player: MusicKit.MusicKitInstance, value: SearchRequestArgs) => {
      const res = await $getCatalogSearch(player, value);
      return res;
    },
    [value],
  );
};
